import { useState } from "react";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";
import { CaretDown, CaretRight, Plus } from "@phosphor-icons/react";
import { Asset, AssetCategory, AssetStatus } from "shared/types/asset";

const STATUS_LABELS: Record<AssetStatus, string> = {
  [AssetStatus.ACTIVE]: "Actief",
  [AssetStatus.INACTIVE]: "Inactief",
  [AssetStatus.MAINTENANCE]: "In onderhoud",
  [AssetStatus.DEFECT]: "Defect",
  [AssetStatus.RETIRED]: "Buiten dienst",
  [AssetStatus.DECOMMISSIONED]: "Ontmanteld",
  [AssetStatus.PENDING_INSTALLATION]: "In afwachting",
};

const STATUS_COLORS: Record<AssetStatus, string> = {
  [AssetStatus.ACTIVE]: "bg-green-100 text-green-800",
  [AssetStatus.INACTIVE]: "bg-gray-100 text-gray-800",
  [AssetStatus.MAINTENANCE]: "bg-yellow-100 text-yellow-800",
  [AssetStatus.DEFECT]: "bg-red-100 text-red-800",
  [AssetStatus.RETIRED]: "bg-gray-200 text-gray-600",
  [AssetStatus.DECOMMISSIONED]: "bg-gray-200 text-gray-600",
  [AssetStatus.PENDING_INSTALLATION]: "bg-blue-100 text-blue-800",
};

interface BuildingAssetCategoryGroupProps {
  category: AssetCategory;
  assets: Asset[];
  onAssetClick: (asset: Asset) => void;
  onAddClick: (category: AssetCategory) => void;
  defaultExpanded?: boolean;
}

export const BuildingAssetCategoryGroup = ({
  category,
  assets,
  onAssetClick,
  onAddClick,
  defaultExpanded = true,
}: BuildingAssetCategoryGroupProps) => {
  const [expanded, setExpanded] = useState(
    defaultExpanded && assets.length > 0
  );
  const isEmpty = assets.length === 0;

  const formatDate = (date?: Date | string) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("nl-BE");
  };

  return (
    <div className={`rounded-lg border ${isEmpty ? "opacity-60" : ""}`}>
      <div
        className="flex cursor-pointer items-center justify-between px-4 py-3"
        onClick={() => !isEmpty && setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          {isEmpty ? (
            <CaretRight className="h-4 w-4 text-muted-foreground" />
          ) : expanded ? (
            <CaretDown className="h-4 w-4" />
          ) : (
            <CaretRight className="h-4 w-4" />
          )}
          <div className="flex items-center gap-2">
            <span
              className="inline-block h-3 w-3 rounded-full"
              style={{ backgroundColor: category.color || "#94a3b8" }}
            />
            <span className="text-sm font-medium">{category.name}</span>
            <Badge variant="secondary" className="text-xs">
              {assets.length}
            </Badge>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onAddClick(category);
          }}
        >
          <Plus className="mr-1 h-3 w-3" />
          Voeg toe
        </Button>
      </div>

      {expanded && assets.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-12">Naam</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Installatie</TableHead>
              <TableHead>Locatie</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {assets.map((asset) => (
              <TableRow
                key={asset.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => onAssetClick(asset)}
              >
                <TableCell className="pl-12 font-medium">
                  {asset.name}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {asset.assetType?.name || "-"}
                </TableCell>
                <TableCell>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[asset.status] || "bg-gray-100 text-gray-800"}`}
                  >
                    {STATUS_LABELS[asset.status] || asset.status}
                  </span>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatDate(asset.installationDate)}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {asset.location || "-"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};
